/**
 * What a credential's expiry date means right now.
 *
 * Shared by the Security column, the partner page and the Overview triage
 * board, because those three must not disagree about whether a token is in
 * trouble.
 *
 * A null date is `none`, never `expired`. Every credential issued before the
 * platform started dating them has no date and keeps working; showing those
 * as expired would bury the ones that genuinely are.
 */

import { type BadgeTone } from '../ui/finixt.js';
import { absoluteDate } from './format.js';

/** Matches `token_expiry_notice_days` on the backend. */
export const EXPIRY_NOTICE_DAYS = 14;

export type ExpiryState = 'none' | 'ok' | 'due' | 'expired';

export interface ExpiryStatus {
  state: ExpiryState;
  label: string;
  tone: BadgeTone;
  /** Negative once past. Null when no date is recorded. */
  daysRemaining: number | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function expiryStatus(
  expiresAt: string | null,
  noticeDays: number = EXPIRY_NOTICE_DAYS,
  now: number = Date.now(),
): ExpiryStatus {
  if (!expiresAt) {
    // Worth showing rather than hiding: an undated credential is one nobody
    // has taken responsibility for rotating.
    return { state: 'none', label: 'no expiry set', tone: 'neutral', daysRemaining: null };
  }

  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) {
    return { state: 'none', label: 'no expiry set', tone: 'neutral', daysRemaining: null };
  }

  const days = Math.ceil((parsed - now) / DAY_MS);
  if (days <= 0) {
    return { state: 'expired', label: 'expired', tone: 'danger', daysRemaining: days };
  }
  if (days <= noticeDays) {
    return {
      state: 'due',
      label: `expires in ${days} day${days === 1 ? '' : 's'}`,
      tone: 'warn',
      daysRemaining: days,
    };
  }
  return {
    state: 'ok',
    label: `expires ${absoluteDate(expiresAt)}`,
    tone: 'ok',
    daysRemaining: days,
  };
}

/**
 * Whether the secret a rotation replaced is still being accepted.
 *
 * Two secrets working at once is a state an operator has to be able to see
 * and end, so it is never folded into the expiry badge.
 */
export function inOverlap(previousRetiresAt: string | null, now: number = Date.now()): boolean {
  if (!previousRetiresAt) return false;
  const parsed = Date.parse(previousRetiresAt);
  return !Number.isNaN(parsed) && parsed > now;
}
